import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Flex } from 'antd'
import { Loading3QuartersOutlined, ReloadOutlined } from '@ant-design/icons'
import { ItemGroupsService } from '../../../services/item-groups.service'
import { useService } from '@pushly/aqe/lib/hooks'
import { usePQueryBuilder } from '@pushly/aqe'

interface CountPreviewProps {
    domainId: number
}

type CountState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'success'; count: number }
    | { status: 'error' }

const DEBOUNCE_MS = 500

/**
 * CountPreview component — displays the number of catalog items matching
 * the current PQuery filter. Uses `usePQueryBuilder()` to reactively track
 * condition count changes and serialize the current query for the count API.
 *
 * States:
 *  - idle: no conditions yet
 *  - loading: count request in flight
 *  - success: "Matches: X items" with refresh button
 *  - error: "Unable to calculate count" with retry button
 */
export const CountPreview: React.FC<CountPreviewProps> = ({ domainId }) => {
    const [state, setState] = useState<CountState>({ status: 'idle' })
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const lastFetchedHashRef = useRef<string | null>(null)

    const { isValid, conditionCount, serialize, queryHash } = usePQueryBuilder()
    const itemGroupsSvc = useService(ItemGroupsService)

    const fetchCount = useCallback(async () => {
        if (!isValid || conditionCount === 0) return

        // Avoid re-fetching if the query hasn't changed since last fetch
        if (lastFetchedHashRef.current === queryHash) return
        lastFetchedHashRef.current = queryHash
        setState({ status: 'loading' })

        try {
            const filtersJson = serialize()
            const response = await itemGroupsSvc.count(domainId, filtersJson)

            if (response.ok && response.data?.count != null) {
                setState({ status: 'success', count: response.data.count })
            } else {
                setState({ status: 'error' })
            }
        } catch {
            setState({ status: 'error' })
        }
    }, [isValid, conditionCount, serialize, domainId, itemGroupsSvc, queryHash])

    // Debounce on conditionCount changes (reactive via usePQueryBuilder)
    useEffect(() => {
        if (conditionCount === 0) {
            setState({ status: 'idle' })
            return
        }

        if (!isValid) return

        // Clear any existing debounce timer
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null
            fetchCount()
        }, DEBOUNCE_MS)

        return () => {
            if (debounceTimerRef.current !== null) {
                clearTimeout(debounceTimerRef.current)
                debounceTimerRef.current = null
            }
        }
    }, [conditionCount, isValid, fetchCount, queryHash])

    const handleRefresh = () => {
        lastFetchedHashRef.current = null // Force re-fetch
        fetchCount()
    }

    if (state.status === 'idle') {
        return null
    }

    if (state.status === 'loading') {
        return (
            <Flex align="center" gap={8}>
                <Loading3QuartersOutlined spin={true} />
                <span>Calculating matches…</span>
            </Flex>
        )
    }

    if (state.status === 'error') {
        return (
            <Flex align="center" gap={8}>
                <span>Unable to calculate count</span>
                <Button type="link" size="small" onClick={handleRefresh}>
                    Retry
                </Button>
            </Flex>
        )
    }

    // success
    return (
        <Flex align="center" gap={8}>
            <span>Matches: {state.count} items</span>
            <Button
                type="text"
                size="small"
                icon={<ReloadOutlined aria-hidden="true" />}
                onClick={handleRefresh}
                aria-label="Refresh count"
            />
        </Flex>
    )
}
